/**
 * Account product module helpers (MDP-548 / ER-10465).
 * Source of truth is Account.product_* exposed on GET account detail
 * and stored in localStorage as `currentAccountDetails` at login.
 */

/**
 * @returns {string|number|null}
 */
export function getStoredCurrentAccountId() {
  try {
    return JSON.parse(localStorage.getItem("currentAccountID"));
  } catch {
    return localStorage.getItem("currentAccountID");
  }
}

/**
 * @param {string|number|null|undefined} a
 * @param {string|number|null|undefined} b
 * @returns {boolean}
 */
export function accountIdsMatch(a, b) {
  if (a == null || b == null) return false;
  return String(a) === String(b);
}

/**
 * Update `currentAccountDetails` when GET/PATCH account detail returns fresh data
 * for the active account.
 * @param {string|number} accountId
 * @param {object|null|undefined} accountDetails
 * @returns {boolean} whether localStorage was updated
 */
export function persistCurrentAccountDetails(accountId, accountDetails) {
  if (!accountDetails || typeof accountDetails !== "object") return false;
  if (!accountIdsMatch(accountId, getStoredCurrentAccountId())) return false;
  localStorage.setItem("currentAccountDetails", JSON.stringify(accountDetails));
  return true;
}

/**
 * Account app only: sync GET/PATCH account detail (and set-product-flag) responses
 * into localStorage for UI gating. Admin portal reads product flags from its own API.
 * @param {{ config?: { url?: string }, data?: unknown }} response
 * @returns {boolean}
 */
export function maybePersistAccountDetailFromAxiosResponse(response) {
  const url = String(response?.config?.url || "").split("?")[0];
  const detailMatch = url.match(/api\/common\/v[12]\/account\/([^/]+)\/?$/);
  const flagMatch = url.match(/api\/common\/v[12]\/account\/([^/]+)\/set-product-flag\/?$/);
  const accountId = detailMatch?.[1] ?? flagMatch?.[1];
  if (!accountId) return false;
  const data = response?.data;
  if (!data || typeof data !== "object" || Array.isArray(data)) return false;
  return persistCurrentAccountDetails(accountId, data);
}

/**
 * @returns {object}
 */
export function getCurrentAccountDetails() {
  try {
    return JSON.parse(localStorage.getItem("currentAccountDetails") || "{}") || {};
  } catch {
    return {};
  }
}

/**
 * Fair Pay module enabled for the current account (admin Products switch).
 * @returns {boolean}
 */
export function isProductFairPayEnabled() {
  return getCurrentAccountDetails().product_fair_pay === true;
}

/**
 * Procedures module enabled for the current account (admin Products switch).
 * @param {object|null|undefined} [accountDetails] Defaults to the stored `currentAccountDetails`.
 * @returns {boolean}
 */
export function isProductProceduresEnabled(accountDetails) {
  const details = accountDetails ?? getCurrentAccountDetails();
  return details.product_procedures === true;
}

/**
 * Readiness product module enabled for the current account (admin Products switch).
 * Stored on GET account detail as `product_readiness` in `currentAccountDetails`.
 * @returns {boolean}
 */
/**
 * @param {object|null|undefined} [accountDetails]
 * @returns {boolean}
 */
export function resolveProductReadinessEnabled(accountDetails) {
  if (
    accountDetails &&
    typeof accountDetails === "object" &&
    typeof accountDetails.product_readiness === "boolean"
  ) {
    return accountDetails.product_readiness !== false;
  }
  return getCurrentAccountDetails().product_readiness !== false;
}

export function isProductReadinessEnabled() {
  return resolveProductReadinessEnabled();
}

/**
 * Refresh currentAccountDetails from GET account so admin product toggles
 * apply without requiring a full re-login.
 * @param {(accountId: string|number) => Promise<{data?: object}>} fetchAccount
 */
export async function refreshCurrentAccountDetails(fetchAccount) {
  if (typeof fetchAccount !== "function") return getCurrentAccountDetails();
  const accountId = getStoredCurrentAccountId();
  if (!accountId) return getCurrentAccountDetails();
  try {
    const res = await fetchAccount(accountId);
    persistCurrentAccountDetails(accountId, res?.data);
  } catch {
    // Keep last-known currentAccountDetails when refresh fails.
  }
  return getCurrentAccountDetails();
}
